import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "~/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/ui/dialog"
import Text from "~/ui/typography"
import { invitationsPath } from "~/common/paths"
import { addConfetti } from "~/common/add-confetti"
import onboarding1 from "~/images/onboarding1"
import onboarding2 from "~/images/onboarding2"
import onboarding3 from "~/images/onboarding3"

interface WelcomeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inviterName: string
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, onOpenChange, inviterName }) => {
  const [showHowItWorks, setShowHowItWorks] = React.useState(false)
  const navigate = useNavigate()

  const handleClose = () => {
    setShowHowItWorks(false)
    onOpenChange(false)
  }

  const handleReadHowItWorks = () => {
    setShowHowItWorks(true)
  }

  const handleCreateInvite = () => {
    navigate(invitationsPath({}))
    handleClose()
  }

  // Trigger confetti when modal opens (only for the welcome view)
  useEffect(() => {
    if (open && !showHowItWorks) {
      const timer = setTimeout(() => {
        addConfetti()
      }, 300) // Small delay to let modal animate in

      return () => clearTimeout(timer)
    }
  }, [open, showHowItWorks])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`!max-h-[90vh] !max-w-[90vw] !overflow-y-auto !p-3 sm:!p-8 ${showHowItWorks ? "sm:!max-h-[80vh] sm:!max-w-4xl" : "sm:!max-w-md"}`}
      >
        {!showHowItWorks ? (
          <>
            <DialogHeader>
              <DialogTitle>Welcome to PostalFun! 👋</DialogTitle>
            </DialogHeader>

            <hr className="my-3" />

            <DialogDescription className="mb-4">
              You've just joined by accepting an invitation from {inviterName} and have been added
              to their contacts.
              <br />— Great to have you here!
            </DialogDescription>

            <DialogFooter className="flex-col space-y-2 sm:flex-row sm:justify-start sm:space-y-0">
              <Button onClick={handleReadHowItWorks}>Read How It Works</Button>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-center text-lg font-semibold">
                Here's how it works
              </DialogTitle>
            </DialogHeader>

            <hr className="my-4" />

            {/* 3-column grid */}
            <div className="mb-4 grid grid-cols-1 gap-3 md:mb-8 md:grid-cols-3 md:gap-6">
              <div>
                <img
                  src={onboarding1.src}
                  alt="Step 1"
                  className="mx-auto mb-3 h-auto w-full max-w-[200px] rounded-lg md:max-w-none"
                />
                <Text as="h4" variant="subheader_bold" className="mb-1">
                  Add More Contacts
                </Text>
                <Text variant="body" className="text-gray-600">
                  Send out your own invitation links to friends, family, colleagues — anyone you
                  want in your contact book. When they join, they’ll be added to your contact book,
                  just like you were to theirs.
                </Text>
              </div>

              <div>
                <img
                  src={onboarding2.src}
                  alt="Step 2"
                  className="mx-auto mb-3 h-auto w-full max-w-[200px] rounded-lg md:max-w-none"
                />
                <Text as="h4" variant="subheader_bold" className="mb-1">
                  Build Your Network
                </Text>
                <Text variant="body" className="text-gray-600">
                  As people accept your invitations, your contact book grows.
                </Text>
              </div>

              <div>
                <img
                  src={onboarding3.src}
                  alt="Step 3"
                  className="mx-auto mb-3 h-auto w-full max-w-[200px] rounded-lg md:max-w-none"
                />
                <Text as="h4" variant="subheader_bold" className="mb-1">
                  Keep Things Current
                </Text>
                <Text variant="body" className="text-gray-600">
                  Everyone manages their own info — so your address book always stays accurate. Each
                  contact includes key details like email, phone number, and crypto wallet address.
                </Text>
              </div>
            </div>

            {/* Call to action section */}
            <div className="flex flex-col items-center justify-center space-y-3 rounded-lg bg-tag-bg p-3 sm:p-8">
              <Text variant="heading4">Ready to get started?</Text>

              <Text variant="body" className="text-gray-600">
                Create your first invite and start building your personal contact book.
              </Text>

              <Button onClick={handleCreateInvite} className="w-full sm:w-auto">
                Create an Invite
              </Button>
            </div>

            <div className="mt-4 text-center">
              <button
                onClick={handleClose}
                className="text-sm text-gray-500 underline hover:text-gray-700"
              >
                Close
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
